import Navigation from "../../common/components/Navigation/Navigation";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "../../common/state/store";
import {
  getProjectTagList,
  getProjectTagListSelector,
  INewProjectFormValues,
  setCurrentTemplateId,
  setNewProject,
} from "../../features/projects/state/projectsSlice";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../../common/state/hooks";
import TemplateCategories from "../../features/projects/components/TemplateCategories";
import {
  getCurrentElements,
  getProjectGroupSize,
  getSelectedProjectGroup,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { FooterButtons } from "../../common/components/FooterButtons";
import { getCurrentUploadsStateList } from "src/features/gallery/state/gallerySlice";
import Chatbot from "../../features/chatbot/components/Chatbot";
import {
  getActiveGptConversation,
  setLastAiModuleContext,
} from "../../features/chatbot/state/chatbotSlice";

export const GET_TEMPLATE_COMPONENT = "templateCompnent";

const SELECTED_BY_DEFAULT_TEMPLATE_ID = 390; // 390 => "From scratch" template

type CreationTab = "templates" | "ai";

const Templates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<CreationTab>("templates");

  const { handleSubmit, control, setValue } = useForm<INewProjectFormValues>();

  useEffect(() => {
    dispatch(getProjectTagList({ componentId: GET_TEMPLATE_COMPONENT }));
    dispatch(setCurrentTemplateId(SELECTED_BY_DEFAULT_TEMPLATE_ID));
    setValue("projectId", SELECTED_BY_DEFAULT_TEMPLATE_ID);
  }, []);

  const { tagList, currentProjectGroup, uploads, activeGptConversation } = useSelector(
    (state: RootState) => {
      return {
        tagList: getProjectTagListSelector(state),
        currentProjectGroup: getSelectedProjectGroup(state),
        uploads: getCurrentUploadsStateList(state),
        activeGptConversation: getActiveGptConversation(state),
      };
    },
  );

  const [formHeight, setFormHeight] = useState(0);

  const onSubmit: SubmitHandler<INewProjectFormValues> = (data) => {
    // TODO: replace this quick fix of WIXAR-3750 to proper langage selector (WIXAR-3595 and its ramifications)
    dispatch(setNewProject({ ...data, languageId: 1 }));
    navigate(`${data.projectId}`);
  };

  const { selectedProjectGroup, currentElements } = useSelector((state: RootState) => {
    return {
      selectedProjectGroup: getSelectedProjectGroup(state),
      currentElements: getCurrentElements(state),
    };
  });

  const [isComputingPgSize, setIsComputingPgSize] = useState(false);
  const [isOverSizeLimitForProjectGroup, setIsOverSizeLimitForProjectGroup] = useState(false);

  useEffect(() => {
    if (currentElements.length > 0) {
      setIsComputingPgSize(true);
      dispatch(
        getProjectGroupSize({
          projectsIds: currentElements.map((pge) => pge.project.id).join(","),
        }),
      )
        .then((res: any) => {
          const projectGroupSize = res.payload.data;
          // If the current project group size is > 10Gb, forbid the project creation
          if (projectGroupSize > 10737418240) {
            setIsOverSizeLimitForProjectGroup(true);
          }
        })
        .finally(() => {
          setIsComputingPgSize(false);
        });
    }
  }, []);

  const handleGenerateProjectFromAi = () => {
    if (!activeGptConversation?.id) return;
    dispatch(
      setLastAiModuleContext({
        conversationId: activeGptConversation.id,
        relatedGptOutputIndex: activeGptConversation.conversation_data.output.length - 1,
        generateBlockadeImages: false,
      }),
    );
    dispatch(
      setNewProject({
        projectId: 0,
        newName: "",
        languageId: 1,
        thumbnailId: 0,
        enabledLanguagesIds: [],
        projectGroupId: currentProjectGroup.id,
      }),
    );
    navigate("ai");
  };

  const getNextDisabledTooltipText = () => {
    if (isComputingPgSize) {
      return t("tooltip.computingPgSize");
    } else if (isOverSizeLimitForProjectGroup) {
      return t("tooltip.cannotCreateProjectProjectGroupTooLarge");
    } else {
      return "";
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const navbarElement = document.getElementById("navbar");
      const uploadBarElement = document.getElementById("upload-bar-overlay");
      const navbarHeight = uploadBarElement?.clientHeight || 0;
      const uploadBarHeight = navbarElement?.clientHeight || 0;
      const windowHeight = document.documentElement.clientHeight;

      setFormHeight(windowHeight - navbarHeight - uploadBarHeight);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [uploads]);

  const navigationTabs = [
    {
      title: t("chooseTemplateType.tabBrowseTemplates"),
      urlEndPoint: "",
      preventNavigation: true,
      isActive: activeTab === "templates",
      onClick: () => setActiveTab("templates"),
    },
    {
      title: t("chooseTemplateType.tabCreateWithAi"),
      urlEndPoint: "",
      preventNavigation: true,
      isActive: activeTab === "ai",
      onClick: () => setActiveTab("ai"),
    },
  ];

  return (
    <>
      <Navigation
        title={t("pages.createProject.title")}
        showGoBackButton
        backCustomRedirectTo={`/courses/${currentProjectGroup.id}/list`}
        tabs={navigationTabs}
      />
      <div className="w-full xl:px-0 ">
        {activeTab === "templates" ? (
          <form
            key={formHeight}
            onSubmit={handleSubmit(onSubmit)}
            style={{ height: `${formHeight}px` }}
            className="flex flex-col justify-between"
          >
            <h1 className="text-2xl font-medium pl-4 pr-4 pt-2">
              {t("chooseTemplateType.title")}
            </h1>
            <p className="mb-6 mt-4 pl-4 pr-4">{t("chooseTemplateType.whatsTemplate")}</p>

            <div className="inline-block w-full pl-4 pr-4 overflow-auto flex-grow  personalize-scroll-visible">
              <TemplateCategories setValue={setValue} control={control} tagList={tagList} />
            </div>

            <div className="bg-gray-100 w-full flex-shrink-0">
              <FooterButtons
                cancelText={t("general.cancel")}
                cancelCb={() => navigate(`/courses/${selectedProjectGroup.id}/list`)}
                backText={t("general.back")}
                backDisabled
                nextText={t("general.next")}
                nextDisabled={isComputingPgSize || isOverSizeLimitForProjectGroup}
                nextDisabledTooltipText={getNextDisabledTooltipText()}
              />
            </div>
          </form>
        ) : (
          <div style={{ height: `${formHeight}px` }} className="flex flex-col">
            <Chatbot
              mode="scenarioHelper"
              activeConversation={null}
              onGenerateProject={handleGenerateProjectFromAi}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Templates;
